import React, { useState, useEffect } from 'react';
import SearchBox from '../../components/SearchComponent';
import TableComponent from '../../components/TableComponent';
import ButtonElement from '../../../Common/ButtonElement';
import AddModal from './AddModal';
import AddSupplier from '../../Inventory/components/AddSupplier';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getInventoryFilters } from '../../../actions/inventory';
import { RiDeleteBin5Line } from 'react-icons/ri';
import { useApiRequest } from '../../../hooks/useApiRequest';
import { appApis } from '../../../constants';
import { DELETE_POP_UP } from '../../../utils';

// Helper function to filter data by search
const filterDataBySearch = (data, search) => {
  const searchLower = search.toLowerCase();
  return data.filter(item => {
    return (
      (item.name || item.type || '').toLowerCase().includes(searchLower) ||
      (item.code || '').toLowerCase().includes(searchLower) ||
      (item.description || '').toLowerCase().includes(searchLower) ||
      (item.title || '').toLowerCase().includes(searchLower) ||
      (item.providerName || '').toLowerCase().includes(searchLower) ||
      (item.warehouseName || '').toLowerCase().includes(searchLower) ||
      (item.providerId || '').toString().toLowerCase().includes(searchLower) ||
      (item.supplierName || '').toLowerCase().includes(searchLower) ||
      (item.supplierId || '').toString().toLowerCase().includes(searchLower)
    );
  });
};

// Helper function to get table headers based on active tab
const getTableHeaders = (activeTab) => {
  switch (activeTab) {
    case 'Inventory Types':
      return ["Name", "Actions"];
    case 'Device Models':
      return ["Name", "Device Type", "Actions"];
    case 'Software Providers':
      return ["Name", "Actions"];
    case 'SIM Providers':
      return ["Name", "Actions"];
    case 'Warehouses':
      return ["Name", "Actions"];
    case 'Supplier':
      return ["Name", "Email", "Mobile Number", "GstNumber", "Address", "Actions"];
    default:
      return ["Name", "Actions"];
  }
};

const deleteApi = {
  "Inventory Types": appApis.deleteInventoryTypes,
  "SIM Providers": appApis.deleteSimProviders,
  "Warehouses": appApis.deleteWarehouses,
  "Device Models": appApis.deleteDeviceModal,
  "Supplier": appApis.deleteSupplier,
  "Software Providers": appApis.deleteSoftwareProvider
};

const MasterData = () => {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('Inventory Types');
  const [masterData, setMasterData] = useState([]);
  const dispatch = useDispatch();
  const { filters } = useSelector(({ inventory }) => ({ filters: inventory.filters }), shallowEqual);
  const {loading , request} = useApiRequest();

  const getTabData = () => {
    let data = [];
    if (activeTab == 'Inventory Types') data = filters?.inventoryTypes ?? [];
    else if (activeTab == 'Device Models') data = filters?.deviceModels ?? [];
    else if (activeTab == 'Software Providers') data = filters?.softwareProviders ?? [];
    else if (activeTab == 'SIM Providers') data = filters?.simProviders ?? [];
    else if (activeTab == 'Warehouses') data = filters?.warehouse ?? [];
    else if (activeTab == 'Supplier') data = filters?.suppliers ?? [];
    setMasterData(data);
  }

  // Fetch data when tab changes
  useEffect(() => {
    getTabData();
  }, [activeTab, filters]);

  // Filter master data by search
  const filteredData = filterDataBySearch(masterData, search);

  const changeTab = (tab) => {
    setActiveTab(tab);
    setSearch(''); // Clear search when changing tabs
  };

  // Render table row based on data structure
  const renderTableRow = (tdData, index) => {
    // For Inventory Types
    if (activeTab === 'Inventory Types') {
      return (
        <tr key={index}>
          <td>{(index ?? 0) + 1}</td>
          <td>
            <div>
              <strong>{tdData.name || tdData.supplierName || '-'}</strong>
            </div>
          </td>
          <td>
            <div>
              {!tdData.isGlobal && (
                <ButtonElement
                  text={<RiDeleteBin5Line size={20} />}
                  cssClass="more-radius px-4"
                  variant="btn-delete-icon"
                  onClick={() => handleDelete(tdData, activeTab)}
                  title="Delete"
                  loading={loading}
                />
              )}
            </div>
          </td>
        </tr>
      );
    }

    // For Supplies tab, render all fields
    if (activeTab === 'Supplier') {
      return (
        <tr key={index}>
          <td>{(index ?? 0) + 1}</td>
          <td>
            <div>
              <strong>{tdData.name || tdData.supplierName || '-'}</strong>
            </div>
          </td>
          <td>{tdData.email || '-'}</td>
          <td>{tdData.number || tdData.mobileNo || tdData.phone || '-'}</td>
          <td>{tdData.gstNumber || tdData.gstNo || '-'}</td>
          <td>{tdData.address || '-'}</td>
          <td>
            <div>
              <ButtonElement
                text={<RiDeleteBin5Line size={20} />}
                cssClass="more-radius px-4"
                variant="btn-delete-icon"
                onClick={() => handleDelete(tdData, activeTab)}
                title="Delete"
                loading={loading}
              />
            </div>
          </td>
        </tr>
      );
    }

    // For Device Models
    if (activeTab === 'Device Models') {
      return (
        <tr key={index}>
          <td>{(index ?? 0) + 1}</td>
          <td>
            <div>
              <strong>{tdData.name || tdData.supplierName || '-'}</strong>
            </div>
          </td>
          <td>{tdData.deviceType || '-'}</td>
          <td>
            <div>
              <ButtonElement
                text={<RiDeleteBin5Line size={20} />}
                cssClass="more-radius px-4"
                variant="btn-delete-icon"
                onClick={() => handleDelete(tdData, activeTab)}
                title="Delete"
                loading={loading}
              />
            </div>
          </td>
        </tr>
      );
    }

    // For other tabs, render only name
    return (
      <tr key={index}>
        <td>{(index ?? 0) + 1}</td>
        <td>
          <div>
            <strong>{tdData?.name ?? ""}</strong>
          </div>
        </td>
        <td>
          <div>
            <ButtonElement
              text={<RiDeleteBin5Line size={20} />}
              cssClass="more-radius px-4"
              variant="btn-delete-icon"
              onClick={() => handleDelete(tdData, activeTab)}
              title="Delete"
              loading={loading}
            />
          </div>
        </td>
      </tr>
    );
  };

  const handleDelete = (item, activeTab) => {
    const apiUrl = deleteApi[activeTab];
    DELETE_POP_UP(() => {
      request({
        url: `${apiUrl}/${item.id}`,
        method: 'DELETE',
        body: { id: item.id }
      }).then(() => {
        // Refresh the data after successful deletion
        dispatch(getInventoryFilters());
      })
    });
  };

  return (
    <div className="user-container">
      <SearchBox
        placeholder={`Search ${activeTab.toLowerCase()}...`}
        className='mb-3'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <TableComponent
        title={activeTab}
        col={8}
        headers={getTableHeaders(activeTab)}
        TabComponent={
          <div className="tabs p-1 rounded d-md-flex justify-content-between text-center">
            {["Inventory Types", "Device Models", "Software Providers", "SIM Providers", "Warehouses", "Supplier"].map((tab, index) => (
              <div
                className={`d-flex justify-content-center align-items-center rounded py-2 flex-fill ${activeTab === tab ? "active" : ""} mx-1`}
                key={index}
                onClick={() => changeTab(tab)}
                style={{ cursor: 'pointer' }}
              >
                <div className="ms-2">{tab}</div>
              </div>
            ))}
          </div>
        }
        data={filteredData}
        render={renderTableRow}
        FilterComponent={
          <div className="d-flex justify-content-end">
            <ButtonElement
              text={activeTab === 'Supplier' ? 'Add Supplier' : `Add ${activeTab.slice(0, -1)}`}
              cssClass="small-radius ms-2 mb-1"
              onClick={() => setShowModal(true)}
              variant="btn-black"
            />
          </div>
        }
      />

      {/* Add Modal */}
      {showModal ? activeTab === 'Supplier' ? (
        <AddSupplier
          showLog={showModal}
          hideModal={() => setShowModal(false)}
          getSuppliers={() => dispatch(getInventoryFilters())}
        />
      ) : (
        <AddModal
          show={showModal}
          onHide={() => setShowModal(false)}
          activeTab={activeTab}
          onSuccess={() => dispatch(getInventoryFilters())}
        />
      ) : null}
    </div>
  );
};

export default MasterData;